// ============================================================
// DashboardLayout — Shell rediseñado (Indigo + Teal)
// ============================================================
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { AuthService } from 'services/AuthService';
import { UserRol } from 'models/User';
import { getConfig } from 'utils/appConfig';
import { useConfigInstitucional } from 'hooks/useConfigInstitucional';
import { I } from 'components/ui';

import UsuariosPage from 'pages/UsuariosPage';
import CursosPage from 'pages/CursosPage';
import GradosPage from 'pages/GradosPage';
import SeccionesPage from 'pages/SeccionesPage';
import MisCursosPage from 'pages/MisCursosPage';
import HorariosPage from 'pages/HorariosPage';
import EstudiantesPage from 'pages/EstudiantesPage';
import MiHorarioPage from 'pages/MiHorarioPage';
import MisCursosEstudiantePage from 'pages/MisCursosEstudiantePage';
import PeriodosPage from 'pages/PeriodosPage';
import CalificacionesPage from 'pages/CalificacionesPage';
import MisNotasPage from 'pages/MisNotasPage';
import AsistenciaDocentePage from 'pages/AsistenciaDocentePage';
import MiAsistenciaPage from 'pages/MiAsistenciaPage';
import HorarioDocentePage from 'pages/HorarioDocentePage';
import MatriculasPage from 'pages/MatriculasPage';
import ConfigPagosPage from 'pages/ConfigPagosPage';
import CobrosPage from 'pages/CobrosPage';
import CobrosAdicionalesPage from 'pages/CobrosAdicionalesPage';
import ConfigSunatPage from 'pages/ConfigSunatPage';
import ConfigFcmPage from 'pages/ConfigFcmPage';
import ExoneracionesPage from 'pages/ExoneracionesPage';
import MisPagosPage from 'pages/MisPagosPage';
import MisHijosPage from 'pages/MisHijosPage';
import DashboardHomePage from 'pages/DashboardHomePage';
import NotificacionesPage from 'pages/NotificacionesPage';
import RegistroMasivoPage from 'pages/RegistroMasivoPage';
import ReportesPage from 'pages/ReportesPage';
import MatriculaMasivaPage from 'pages/MatriculaMasivaPage';
import CredencialesEstudiantesPage from 'pages/CredencialesEstudiantesPage';
import DocentesPage from 'pages/DocentesPage';
import ImpresionHorariosPage from 'pages/ImpresionHorariosPage';
import TicketsPage from 'pages/TicketsPage';
import ConfiguracionPage from 'pages/ConfiguracionPage';
import NominaMatriculaPage from 'pages/NominaMatriculaPage';
import PromocionPage from 'pages/PromocionPage';
import OrdenMeritoPage from 'pages/OrdenMeritoPage';
import TrasladosPage from 'pages/TrasladosPage';
import CompetenciasConfigPage from 'pages/CompetenciasConfigPage';
import NotasCompetenciaPage from 'pages/NotasCompetenciaPage';
import ConfigInstitucionPage from 'pages/ConfigInstitucionPage';
import WizardMatriculaPage from 'pages/WizardMatriculaPage';
import BoletasMasivaPage from 'pages/BoletasMasivaPage';
import PorteriaPage from 'pages/PorteriaPage';
import AsistenciaDiariaListPage from 'pages/AsistenciaDiariaListPage';
import MiAsistenciaDiariaPage from 'pages/MiAsistenciaDiariaPage';
import FotocheckPage from 'pages/FotocheckPage';
import AgendaPsicologoPage from 'pages/AgendaPsicologoPage';
import DerivacionesPage from 'pages/DerivacionesPage';
import CasosConvivenciaPage from 'pages/CasosConvivenciaPage';
import { NotificacionProvider } from 'contexts/NotificacionContext';
import CampanaNotificaciones from 'components/notifications/CampanaNotificaciones';
import NotificacionToast from 'components/notifications/NotificacionToast';

// ============================================================
// Tipos del menú
// ============================================================
type IconName = keyof typeof I;

interface SubMenuItem { label: string; key: string; }
interface MenuItem {
  label:    string;
  icon:     IconName;
  key:      string;
  children?: SubMenuItem[];
}
interface MenuSection {
  section: string;
  items:   MenuItem[];
}

// ============================================================
// Menú por rol — agrupado en secciones
// ============================================================
const menuByRol: Record<UserRol, MenuSection[]> = {
  ADMIN: [
    { section: 'Principal', items: [
      { label: 'Panel Principal', icon: 'Dashboard', key: 'dashboard' },
    ]},
    { section: 'Académico', items: [
      { label: 'Usuarios',   icon: 'Users',    key: 'usuarios_group', children: [
        { label: 'Listado',         key: 'usuarios'        },
        { label: 'Docentes',        key: 'docentes'        },
        { label: 'Registro Masivo', key: 'registro-masivo' },
      ]},
      { label: 'Académico',  icon: 'BookOpen', key: 'academico_group', children: [
        { label: 'Cursos',       key: 'academico'           },
        { label: 'Grados',       key: 'grados'              },
        { label: 'Secciones',    key: 'secciones'           },
        { label: 'Periodos',     key: 'periodos'            },
        { label: 'Competencias', key: 'competencias-config' },
      ]},
      { label: 'Matrículas', icon: 'CalendarCheck', key: 'matriculas_group', children: [
        { label: '⭐ Asistente de Matrícula', key: 'wizard-matricula' },
        { label: 'Listado',                   key: 'matriculas'      },
        { label: 'Matrícula Masiva',          key: 'matricula-masiva' },
        { label: 'Nómina de Matrícula',       key: 'nominas-matricula' },
      ]},
      { label: 'Horarios',   icon: 'Calendar', key: 'horarios_group', children: [
        { label: 'Gestión',   key: 'horarios'           },
        { label: 'Impresión', key: 'impresion-horarios' },
      ]},
      { label: 'Fin de Año', icon: 'Award', key: 'fin_anio_group', children: [
        { label: 'Promoción / Cierre', key: 'promocion'    },
        { label: 'Orden de Mérito',    key: 'orden-merito' },
        { label: 'Traslados',          key: 'traslados'    },
      ]},
    ]},
    { section: 'Asistencia diaria', items: [
      { label: 'Portería',        icon: 'CheckCircle', key: 'porteria' },
      { label: 'Listado diario',  icon: 'ClipboardList', key: 'asistencia-diaria' },
      { label: 'Fotochecks',      icon: 'CreditCard', key: 'fotocheck' },
    ]},
    { section: 'Psicología', items: [
      { label: 'Agenda',         icon: 'Calendar',      key: 'psicologia-agenda' },
      { label: 'Derivaciones',   icon: 'Send',          key: 'psicologia-derivaciones' },
      { label: 'Convivencia',    icon: 'AlertCircle',   key: 'casos-convivencia' },
    ]},
    { section: 'Sistema', items: [
      { label: 'Credenciales',   icon: 'Lock',      key: 'credenciales-estudiantes' },
      { label: 'Notificaciones', icon: 'Bell',      key: 'notificaciones' },
      { label: 'Reportes',       icon: 'BarChart',  key: 'reportes' },
      { label: 'Soporte',        icon: 'HelpCircle',key: 'tickets' },
      { label: 'Configuración',  icon: 'Settings',  key: 'configuracion_group', children: [
        { label: 'General',               key: 'configuracion'        },
        { label: 'Config. Institucional', key: 'config-institucional' },
        { label: 'Tarifas y Descuentos',  key: 'config-pagos'         },
        { label: 'Config. SUNAT',         key: 'config-sunat'         },
        { label: 'Notificaciones Push',   key: 'config-fcm'           },
      ]},
    ]},
  ],
  DIRECTOR: [
    { section: 'Principal', items: [
      { label: 'Panel Principal', icon: 'Dashboard', key: 'dashboard' },
    ]},
    { section: 'Académico', items: [
      { label: 'Usuarios',   icon: 'Users',    key: 'usuarios_group', children: [
        { label: 'Listado',         key: 'usuarios'        },
        { label: 'Docentes',        key: 'docentes'        },
        { label: 'Registro Masivo', key: 'registro-masivo' },
      ]},
      { label: 'Académico',  icon: 'BookOpen', key: 'academico_group', children: [
        { label: 'Cursos',    key: 'academico'  },
        { label: 'Grados',    key: 'grados'     },
        { label: 'Secciones', key: 'secciones'  },
        { label: 'Periodos',  key: 'periodos'   },
      ]},
      { label: 'Matrículas', icon: 'CalendarCheck', key: 'matriculas_group', children: [
        { label: 'Listado',             key: 'matriculas'        },
        { label: 'Matrícula Masiva',    key: 'matricula-masiva'  },
        { label: 'Nómina de Matrícula', key: 'nominas-matricula' },
      ]},
      { label: 'Boletas',    icon: 'Award',        key: 'boletas-masiva' },
      { label: 'Horarios',   icon: 'Calendar', key: 'horarios_group', children: [
        { label: 'Impresión', key: 'impresion-horarios' },
      ]},
      { label: 'Fin de Año', icon: 'Award',    key: 'fin_anio_group', children: [
        { label: 'Promoción / Cierre', key: 'promocion'    },
        { label: 'Orden de Mérito',    key: 'orden-merito' },
        { label: 'Traslados',          key: 'traslados'    },
      ]},
    ]},
    { section: 'Asistencia diaria', items: [
      { label: 'Portería',        icon: 'CheckCircle',   key: 'porteria' },
      { label: 'Listado diario',  icon: 'ClipboardList', key: 'asistencia-diaria' },
      { label: 'Fotochecks',      icon: 'CreditCard',    key: 'fotocheck' },
    ]},
    { section: 'Psicología', items: [
      { label: 'Agenda',         icon: 'Calendar',      key: 'psicologia-agenda' },
      { label: 'Derivaciones',   icon: 'Send',          key: 'psicologia-derivaciones' },
      { label: 'Convivencia',    icon: 'AlertCircle',   key: 'casos-convivencia' },
    ]},
    { section: 'Sistema', items: [
      { label: 'Credenciales',   icon: 'Lock',     key: 'credenciales-estudiantes' },
      { label: 'Notificaciones', icon: 'Bell',     key: 'notificaciones' },
      { label: 'Reportes',       icon: 'BarChart', key: 'reportes' },
      { label: 'Soporte',        icon: 'HelpCircle', key: 'tickets' },
      { label: 'Configuración',  icon: 'Settings', key: 'configuracion' },
    ]},
  ],
  DOCENTE: [
    { section: 'Principal', items: [
      { label: 'Panel Principal', icon: 'Dashboard', key: 'dashboard' },
    ]},
    { section: 'Mis clases', items: [
      { label: 'Horarios',           icon: 'Calendar',    key: 'horarios' },
      { label: 'Mis Cursos',         icon: 'BookOpen',    key: 'cursos' },
      { label: 'Asistencia',         icon: 'CheckCircle', key: 'asistencia' },
      { label: 'Calificaciones',     icon: 'Award',       key: 'calificaciones' },
      { label: 'Notas Competencias', icon: 'Star',        key: 'notas-competencias' },
    ]},
    { section: 'Bienestar', items: [
      { label: 'Derivar a psicología', icon: 'Send',        key: 'psicologia-derivaciones' },
      { label: 'Convivencia',          icon: 'AlertCircle', key: 'casos-convivencia' },
    ]},
    { section: 'Sistema', items: [
      { label: 'Notificaciones', icon: 'Bell',     key: 'notificaciones' },
      { label: 'Reportes',       icon: 'BarChart', key: 'reportes' },
    ]},
  ],
  ESTUDIANTE: [
    { section: 'Principal', items: [
      { label: 'Panel Principal', icon: 'Dashboard', key: 'dashboard' },
    ]},
    { section: 'Mis estudios', items: [
      { label: 'Mi Horario',           icon: 'Calendar',      key: 'horario' },
      { label: 'Mis Cursos',           icon: 'BookOpen',      key: 'cursos' },
      { label: 'Mis Notas',            icon: 'Award',         key: 'notas' },
      { label: 'Mi Asistencia',        icon: 'CheckCircle',   key: 'asistencia' },
      { label: 'Asistencia Diaria',    icon: 'ClipboardList', key: 'mi-asistencia-diaria' },
    ]},
    { section: 'Pagos', items: [
      { label: 'Mis Pagos',      icon: 'Wallet',      key: 'pagos' },
    ]},
    { section: 'Sistema', items: [
      { label: 'Notificaciones', icon: 'Bell',        key: 'notificaciones' },
    ]},
  ],
  PADRE: [
    { section: 'Principal', items: [
      { label: 'Panel Principal', icon: 'Dashboard', key: 'dashboard' },
    ]},
    { section: 'Mi hijo/a', items: [
      { label: 'Mis Hijos', icon: 'Users', key: 'hijos' },
    ]},
    { section: 'Sistema', items: [
      { label: 'Notificaciones', icon: 'Bell',        key: 'notificaciones' },
    ]},
  ],
  SECRETARIA: [
    { section: 'Principal', items: [
      { label: 'Panel Principal', icon: 'Dashboard', key: 'dashboard' },
    ]},
    { section: 'Académico', items: [
      { label: 'Estudiantes',  icon: 'Users',          key: 'estudiantes_group', children: [
        { label: 'Listado',         key: 'estudiantes'     },
        { label: 'Registro Masivo', key: 'registro-masivo' },
      ]},
      { label: 'Cursos',       icon: 'BookOpen',       key: 'academico' },
      { label: 'Matrículas',   icon: 'CalendarCheck',  key: 'matriculas_group', children: [
        { label: '⭐ Asistente de Matrícula', key: 'wizard-matricula' },
        { label: 'Listado',                   key: 'matriculas'      },
        { label: 'Matrícula Masiva',          key: 'matricula-masiva' },
        { label: 'Nómina de Matrícula',       key: 'nominas-matricula' },
      ]},
      { label: 'Boletas',      icon: 'Award',          key: 'boletas-masiva' },
      { label: 'Horarios',     icon: 'Calendar',       key: 'horarios_group', children: [
        { label: 'Gestión',    key: 'horarios'           },
        { label: 'Impresión',  key: 'impresion-horarios' },
      ]},
      { label: 'Traslados',    icon: 'Send',           key: 'traslados' },
    ]},
    { section: 'Finanzas', items: [
      { label: 'Pagos',        icon: 'DollarSign',     key: 'pagos_group', children: [
        { label: 'Cobros',              key: 'cobros'              },
        { label: 'Cobros Adicionales',  key: 'cobros-adicionales'  },
        { label: 'Exoneraciones',       key: 'exoneraciones'       },
      ]},
    ]},
    { section: 'Asistencia diaria', items: [
      { label: 'Portería',        icon: 'CheckCircle',   key: 'porteria' },
      { label: 'Listado diario',  icon: 'ClipboardList', key: 'asistencia-diaria' },
      { label: 'Fotochecks',      icon: 'CreditCard',    key: 'fotocheck' },
    ]},
    { section: 'Bienestar', items: [
      { label: 'Derivar a psicología', icon: 'Send',        key: 'psicologia-derivaciones' },
      { label: 'Convivencia',          icon: 'AlertCircle', key: 'casos-convivencia' },
    ]},
    { section: 'Sistema', items: [
      { label: 'Credenciales',   icon: 'Lock',     key: 'credenciales-estudiantes' },
      { label: 'Notificaciones', icon: 'Bell',     key: 'notificaciones' },
      { label: 'Reportes',       icon: 'BarChart', key: 'reportes' },
    ]},
  ],
  AUXILIAR: [
    { section: 'Asistencia diaria', items: [
      { label: 'Portería',        icon: 'CheckCircle',   key: 'porteria' },
      { label: 'Listado diario',  icon: 'ClipboardList', key: 'asistencia-diaria' },
    ]},
    { section: 'Bienestar', items: [
      { label: 'Derivar a psicología', icon: 'Send',        key: 'psicologia-derivaciones' },
      { label: 'Convivencia',          icon: 'AlertCircle', key: 'casos-convivencia' },
    ]},
    { section: 'Sistema', items: [
      { label: 'Notificaciones', icon: 'Bell', key: 'notificaciones' },
    ]},
  ],
  PSICOLOGO: [
    { section: 'Principal', items: [
      { label: 'Panel Principal', icon: 'Dashboard', key: 'dashboard' },
    ]},
    { section: 'Atención', items: [
      { label: 'Agenda',          icon: 'Calendar',      key: 'psicologia-agenda' },
      { label: 'Derivaciones',    icon: 'Send',          key: 'psicologia-derivaciones' },
      { label: 'Convivencia',     icon: 'AlertCircle',   key: 'casos-convivencia' },
    ]},
    { section: 'Sistema', items: [
      { label: 'Notificaciones', icon: 'Bell', key: 'notificaciones' },
    ]},
  ],
};

// ============================================================
// Filtrado del menú por flags de configuración
// ============================================================
// Items que aparecen solo si EVAL_USA_COMPETENCIAS=true.
const KEYS_REQUIEREN_COMPETENCIAS = new Set(['notas-competencias', 'competencias-config']);

const filterMenuByFlags = (sections: MenuSection[], usaCompetencias: boolean): MenuSection[] => {
  if (usaCompetencias) return sections;
  return sections.map(section => ({
    ...section,
    items: section.items
      .filter(item => !KEYS_REQUIEREN_COMPETENCIAS.has(item.key))
      .map(item => item.children
        ? { ...item, children: item.children.filter(c => !KEYS_REQUIEREN_COMPETENCIAS.has(c.key)) }
        : item
      ),
  }));
};

// ============================================================
// Etiquetas y colores de rol (badges)
// ============================================================
const rolLabel: Record<UserRol, string> = {
  ADMIN:      'Administrador',
  DIRECTOR:   'Director',
  DOCENTE:    'Docente',
  ESTUDIANTE: 'Estudiante',
  PADRE:      'Apoderado',
  SECRETARIA: 'Secretaría',
  AUXILIAR:   'Auxiliar',
  PSICOLOGO:  'Psicólogo',
};

const rolGradient: Record<UserRol, string> = {
  ADMIN:      'bg-role-admin',
  DIRECTOR:   'bg-role-director',
  DOCENTE:    'bg-role-docente',
  ESTUDIANTE: 'bg-role-estudiante',
  PADRE:      'bg-role-padre',
  SECRETARIA: 'bg-role-admin',
  AUXILIAR:   'bg-role-docente',
  PSICOLOGO:  'bg-role-admin',
};

// ============================================================
// Etiquetas para breadcrumbs (mapa key → label)
// ============================================================
const CRUMB_LABELS: Record<string, string> = {
  dashboard: 'Panel Principal',
  usuarios: 'Usuarios', docentes: 'Docentes', 'registro-masivo': 'Registro Masivo',
  academico: 'Cursos', grados: 'Grados', secciones: 'Secciones', periodos: 'Periodos',
  'competencias-config': 'Competencias',
  matriculas: 'Matrículas', 'matricula-masiva': 'Matrícula Masiva',
  'nominas-matricula': 'Nómina de Matrícula',
  'wizard-matricula': 'Asistente de Matrícula',
  horarios: 'Horarios', 'impresion-horarios': 'Impresión de Horarios',
  'fin_anio_group': 'Fin de Año',
  promocion: 'Promoción / Cierre', 'orden-merito': 'Orden de Mérito',
  traslados: 'Traslados',
  'credenciales-estudiantes': 'Credenciales',
  notificaciones: 'Notificaciones', reportes: 'Reportes', tickets: 'Soporte',
  configuracion: 'Configuración', 'config-institucional': 'Config. Institucional',
  'config-pagos': 'Tarifas y Descuentos', 'config-sunat': 'Config. SUNAT',
  'config-fcm': 'Notificaciones Push (FCM)',
  estudiantes: 'Estudiantes', cursos: 'Cursos',
  cobros: 'Cobros', 'cobros-adicionales': 'Cobros Adicionales', exoneraciones: 'Exoneraciones',
  asistencia: 'Asistencia', calificaciones: 'Calificaciones',
  'notas-competencias': 'Notas por Competencias',
  'boletas-masiva':     'Boletas de Notas',
  horario: 'Mi Horario', notas: 'Mis Notas', pagos: 'Pagos',
  hijos: 'Mis Hijos',
  porteria:               'Portería',
  'asistencia-diaria':    'Asistencia Diaria',
  'mi-asistencia-diaria': 'Mi Asistencia Diaria',
  fotocheck:              'Fotochecks',
  'psicologia-agenda':       'Agenda de Psicología',
  'psicologia-derivaciones': 'Derivaciones',
  'casos-convivencia':       'Convivencia Escolar',
};

// ============================================================
// Modal de cambio de contraseña (estilo nuevo)
// ============================================================
const CambiarPasswordModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [form, setForm] = useState({
    passwordActual: '', passwordNueva: '', passwordConfirmacion: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPwd, setShowPwd] = useState<Record<string, boolean>>({});

  const setField = (k: string, v: string) => {
    setForm(p => ({ ...p, [k]: v }));
    setErrors(p => { const n = { ...p }; delete n[k]; return n; });
    setApiError(null);
  };

  const handleSubmit = async () => {
    const e: Record<string, string> = {};
    if (!form.passwordActual.trim())   e.passwordActual = 'Requerido';
    if (form.passwordNueva.length < 6) e.passwordNueva  = 'Mínimo 6 caracteres';
    if (form.passwordNueva !== form.passwordConfirmacion)
      e.passwordConfirmacion = 'Las contraseñas no coinciden';
    if (Object.keys(e).length) { setErrors(e); return; }

    setSubmitting(true); setApiError(null);
    try {
      await AuthService.changePassword(form);
      setSuccess(true);
    } catch (err: unknown) {
      setApiError(err instanceof Error ? err.message : 'Error al cambiar la contraseña');
    } finally { setSubmitting(false); }
  };

  const PwdField: React.FC<{ field: keyof typeof form; label: string }> = ({ field, label }) => (
    <div className="field">
      <label>{label} <span className="required">*</span></label>
      <div className="relative">
        <input
          type={showPwd[field] ? 'text' : 'password'}
          value={form[field]}
          onChange={e => setField(field, e.target.value)}
          className="input pr-10"
        />
        <button
          type="button"
          onClick={() => setShowPwd(p => ({ ...p, [field]: !p[field] }))}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700"
        >
          {showPwd[field] ? <I.EyeOff size={16} /> : <I.Eye size={16} />}
        </button>
      </div>
      {errors[field] && <p className="text-xs text-red-600">{errors[field]}</p>}
    </div>
  );

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <div className="modal-head">
          <div>
            <h2>Cambiar contraseña</h2>
            <div className="sub">Usa una contraseña fuerte de mínimo 6 caracteres</div>
          </div>
          <button onClick={onClose} className="sae-icon-btn"><I.X /></button>
        </div>
        <div className="modal-body">
          {success ? (
            <div className="text-center py-6">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-100 mb-4 text-green-600">
                <I.CheckCircle size={28} />
              </div>
              <p className="text-ink-900 font-bold text-lg m-0">¡Contraseña actualizada!</p>
              <p className="text-ink-500 text-sm mt-1">Ya puedes seguir usando el sistema.</p>
            </div>
          ) : (
            <div className="grid gap-4">
              <PwdField field="passwordActual"       label="Contraseña actual" />
              <PwdField field="passwordNueva"        label="Nueva contraseña" />
              <PwdField field="passwordConfirmacion" label="Confirmar nueva contraseña" />
              {apiError && (
                <div className="rounded-r-sm bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                  {apiError}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>
            {success ? 'Cerrar' : 'Cancelar'}
          </button>
          {!success && (
            <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Guardando…' : 'Guardar'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Sidebar
// ============================================================
interface SidebarProps {
  rol: UserRol;
  sections: MenuSection[];
  activeKey: string;
  setActiveKey: (k: string) => void;
  user: { nombres: string; apellidos: string };
  drawerOpen: boolean;
  closeDrawer: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ rol, sections, activeKey, setActiveKey, user, drawerOpen, closeDrawer }) => {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    sections.forEach(s => s.items.forEach(it => {
      if (it.children) {
        init[it.key] = it.children.some(c => c.key === activeKey);
      }
    }));
    return init;
  });
  const initials = `${user.nombres[0] || ''}${user.apellidos[0] || ''}`.toUpperCase();
  const fullName = `${user.nombres} ${user.apellidos}`.trim();

  const toggleGroup = (k: string) => setOpenGroups(p => ({ ...p, [k]: !p[k] }));

  const handleNav = (k: string) => {
    setActiveKey(k);
    closeDrawer();
  };

  return (
    <>
      <aside className={`sae-sidebar ${drawerOpen ? 'drawer-open' : ''}`}>
        {/* ── Header (fijo arriba) ── */}
        <div className="sae-sidebar-header">
          {/* Brand */}
          <div className="sae-brand">
            <div className="sae-brand-mark">SAE</div>
            <div className="sae-brand-text">
              <b>SAE Educativo</b>
              <span>{getConfig().institucionNombre || 'Institución'}</span>
            </div>
          </div>

          {/* Badge de rol */}
          <div className="px-3.5 pb-3">
            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] font-bold uppercase tracking-wide text-white ${rolGradient[rol]}`}>
              <I.Award size={11} /> {rolLabel[rol]}
            </div>
          </div>
        </div>

        {/* ── Navegación (área scrolleable) ── */}
        <nav className="sae-sidebar-nav">
          {sections.map(section => (
            <React.Fragment key={section.section}>
              <div className="sae-nav-section">{section.section}</div>
              {section.items.map(item => {
                const IconC = I[item.icon];
                const hasChildren = !!item.children?.length;
                const isOpen = openGroups[item.key];
                const isActive = !hasChildren && activeKey === item.key;
                const groupHasActive = hasChildren && item.children!.some(c => c.key === activeKey);

                return (
                  <React.Fragment key={item.key}>
                    <button
                      className={`sae-nav-item ${isActive || groupHasActive ? 'active' : ''}`}
                      onClick={() => hasChildren ? toggleGroup(item.key) : handleNav(item.key)}
                      type="button"
                    >
                      <IconC />
                      <span>{item.label}</span>
                      {hasChildren && (
                        <I.ChevronDown
                          size={14}
                          style={{
                            marginLeft: 'auto',
                            transition: 'transform .2s',
                            transform: isOpen ? 'rotate(180deg)' : 'none',
                            opacity: 0.6,
                          }}
                        />
                      )}
                    </button>

                    {hasChildren && isOpen && (
                      <div style={{ paddingLeft: 14 }}>
                        {item.children!.map(sub => (
                          <button
                            key={sub.key}
                            className={`sae-nav-item ${activeKey === sub.key ? 'active' : ''}`}
                            onClick={() => handleNav(sub.key)}
                            style={{ paddingLeft: 28, fontSize: 12.5 }}
                            type="button"
                          >
                            <span style={{
                              width: 4, height: 4, borderRadius: '50%',
                              background: 'currentColor', opacity: 0.4,
                              marginRight: -2,
                            }} />
                            <span>{sub.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </React.Fragment>
          ))}
        </nav>

        {/* ── User card (fijo al pie) ── */}
        <div className="sae-user-card">
          <div className={`avatar ${rolGradient[rol]}`} style={{ borderRadius: 10 }}>
            {initials}
          </div>
          <div className="info">
            <b>{fullName}</b>
            <span>{rolLabel[rol]}</span>
          </div>
        </div>
      </aside>

      {/* Backdrop drawer */}
      {drawerOpen && (
        <div className="sae-sidebar-backdrop md:hidden" onClick={closeDrawer} />
      )}
    </>
  );
};

// ============================================================
// Topbar
// ============================================================
interface TopbarProps {
  activeKey:    string;
  rol:          UserRol;
  user:         { nombres: string; apellidos: string };
  onMenuClick:  () => void;
  onNavigate:   (k: string) => void;
  onLogout:     () => void;
  onChangePwd:  () => void;
}

const Topbar: React.FC<TopbarProps> = ({ activeKey, rol, user, onMenuClick, onNavigate, onLogout, onChangePwd }) => {
  const [openUser, setOpenUser] = useState(false);
  const userRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (userRef.current && !userRef.current.contains(e.target as Node)) {
        setOpenUser(false);
      }
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, []);

  const initials = `${user.nombres[0] || ''}${user.apellidos[0] || ''}`.toUpperCase();
  const fullName = `${user.nombres} ${user.apellidos}`.trim();
  const crumbLabel = CRUMB_LABELS[activeKey] || 'Página';

  return (
    <div className="sae-topbar">
      <button onClick={onMenuClick} className="sae-menu-btn" aria-label="Menu">
        <I.Menu />
      </button>

      <div className="sae-crumbs">
        <span className="sae-crumb-hide">SAE</span>
        <span className="sep sae-crumb-hide">/</span>
        <b>{crumbLabel}</b>
      </div>

      <div className="sae-search">
        <I.Search />
        <input placeholder="Buscar estudiantes, cursos, pagos…" />
      </div>

      <CampanaNotificaciones onVerTodas={() => onNavigate('notificaciones')} />

      <div ref={userRef} className="relative flex-shrink-0">
        <button
          className={`avatar ${rolGradient[rol]} cursor-pointer`}
          style={{ border: 'none' }}
          onClick={() => setOpenUser(o => !o)}
          aria-label="Mi cuenta"
        >
          {initials}
        </button>

        {openUser && (
          <div
            className="absolute right-0 mt-2 bg-white rounded-r border shadow-lift overflow-hidden"
            style={{ width: 240, zIndex: 60, borderColor: 'var(--border)' }}
          >
            <div className="px-3 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
              <div className="row" style={{ gap: 10 }}>
                <span className={`avatar ${rolGradient[rol]}`}>{initials}</span>
                <div className="grid min-w-0">
                  <b className="text-[13px] leading-tight truncate">{fullName}</b>
                  <span className="text-[11px] text-ink-500">{rolLabel[rol]}</span>
                </div>
              </div>
            </div>
            <button
              className="sae-nav-item text-ink-700 hover:bg-surface-alt"
              style={{ color: 'var(--ink-700)', borderRadius: 0 }}
              onClick={() => { setOpenUser(false); onChangePwd(); }}
            >
              <I.Lock size={16} /> <span className="text-[13px]">Cambiar contraseña</span>
            </button>
            <div style={{ height: 1, background: 'var(--border)' }} />
            <button
              className="sae-nav-item hover:bg-red-50"
              style={{ color: 'var(--danger)', borderRadius: 0 }}
              onClick={() => { setOpenUser(false); onLogout(); }}
            >
              <I.LogOut size={16} /> <span className="text-[13px] font-semibold">Cerrar sesión</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// ModuleRenderer — preserva la lógica original
// ============================================================
interface ModuleProps {
  activeKey: string;
  rol:       UserRol;
  user:      { nombres: string; apellidos: string };
  onNavigate: (k: string) => void;
}

const ModuleRenderer: React.FC<ModuleProps> = ({ activeKey, rol, onNavigate }) => {
  switch (activeKey) {
    case 'dashboard':                  return <DashboardHomePage onNavigate={onNavigate} />;
    case 'usuarios':                   return <UsuariosPage />;
    case 'registro-masivo':            return <RegistroMasivoPage />;
    case 'academico':                  return <CursosPage />;
    case 'grados':                     return <GradosPage />;
    case 'secciones':                  return <SeccionesPage />;
    case 'periodos':                   return <PeriodosPage />;
    case 'cursos':
      return rol === 'ESTUDIANTE' ? <MisCursosEstudiantePage /> : <MisCursosPage />;
    case 'horarios':
      return rol === 'DOCENTE' ? <HorarioDocentePage /> : <HorariosPage />;
    case 'impresion-horarios':         return <ImpresionHorariosPage />;
    case 'estudiantes':                return <EstudiantesPage />;
    case 'matriculas':                 return <MatriculasPage onNavigate={onNavigate} />;
    case 'matricula-masiva':           return <MatriculaMasivaPage />;
    case 'wizard-matricula':           return <WizardMatriculaPage onFinish={() => onNavigate('matriculas')} />;
    case 'credenciales-estudiantes':   return <CredencialesEstudiantesPage />;
    case 'docentes':                   return <DocentesPage />;
    case 'config-pagos':               return <ConfigPagosPage />;
    case 'cobros':                     return <CobrosPage />;
    case 'cobros-adicionales':         return <CobrosAdicionalesPage />;
    case 'exoneraciones':              return <ExoneracionesPage />;
    case 'horario':                    return <MiHorarioPage />;
    case 'asistencia':
      return rol === 'ESTUDIANTE' ? <MiAsistenciaPage /> : <AsistenciaDocentePage />;
    case 'calificaciones':             return <CalificacionesPage onNavigate={onNavigate} />;
    case 'notas':                      return <MisNotasPage />;
    case 'pagos':                      return <MisPagosPage />;
    case 'hijos':                      return <MisHijosPage />;
    case 'notificaciones':             return <NotificacionesPage />;
    case 'reportes':                   return <ReportesPage />;
    case 'tickets':                    return <TicketsPage />;
    case 'configuracion':              return <ConfiguracionPage />;
    case 'nominas-matricula':          return <NominaMatriculaPage />;
    case 'promocion':                  return <PromocionPage />;
    case 'orden-merito':               return <OrdenMeritoPage />;
    case 'traslados':                  return <TrasladosPage />;
    case 'competencias-config':        return <CompetenciasConfigPage />;
    case 'notas-competencias':         return <NotasCompetenciaPage />;
    case 'boletas-masiva':             return <BoletasMasivaPage />;
    case 'config-institucional':       return <ConfigInstitucionPage />;
    case 'config-sunat':               return <ConfigSunatPage />;
    case 'config-fcm':                 return <ConfigFcmPage />;
    case 'porteria':                   return <PorteriaPage />;
    case 'asistencia-diaria':          return <AsistenciaDiariaListPage />;
    case 'mi-asistencia-diaria':       return <MiAsistenciaDiariaPage />;
    case 'fotocheck':                  return <FotocheckPage />;
    case 'psicologia-agenda':          return <AgendaPsicologoPage />;
    case 'psicologia-derivaciones':    return <DerivacionesPage />;
    case 'casos-convivencia':          return <CasosConvivenciaPage />;
    default:                           return <DashboardHomePage onNavigate={onNavigate} />;
  }
};

// ============================================================
// DashboardLayout — root
// ============================================================
const DashboardLayout: React.FC = () => {
  const { user, logout } = useAuth();
  // El AUXILIAR aterriza en portería; el PSICOLOGO en su agenda.
  const initialKey =
    user?.rol === 'AUXILIAR'  ? 'porteria' :
    user?.rol === 'PSICOLOGO' ? 'psicologia-agenda' :
    'dashboard';
  const [activeKey, setActiveKey] = useState(initialKey);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showChangePwd, setShowChangePwd] = useState(false);

  // Lee flags reactivos para filtrar el menú (notas-competencias /
  // competencias-config se ocultan si EVAL_USA_COMPETENCIAS=false).
  const { getValor } = useConfigInstitucional();
  const usaCompetencias = getValor('EVAL_USA_COMPETENCIAS') === 'true';
  const sections = useMemo(
    () => filterMenuByFlags(menuByRol[user?.rol ?? 'ADMIN'] ?? [], usaCompetencias),
    [user?.rol, usaCompetencias],
  );

  if (!user) return null;

  return (
    <NotificacionProvider>
      <div className={`sae-app ${drawerOpen ? 'drawer-open' : ''}`}>
        <Sidebar
          rol={user.rol}
          sections={sections}
          activeKey={activeKey}
          setActiveKey={setActiveKey}
          user={{ nombres: user.nombres, apellidos: user.apellidos }}
          drawerOpen={drawerOpen}
          closeDrawer={() => setDrawerOpen(false)}
        />

        <div className="sae-main">
          <Topbar
            activeKey={activeKey}
            rol={user.rol}
            user={{ nombres: user.nombres, apellidos: user.apellidos }}
            onMenuClick={() => setDrawerOpen(o => !o)}
            onNavigate={k => { setActiveKey(k); setDrawerOpen(false); }}
            onLogout={logout}
            onChangePwd={() => setShowChangePwd(true)}
          />

          <main key={activeKey} className="sae-content fade-in">
            <div className="sae-content-inner">
              <ModuleRenderer
                activeKey={activeKey}
                rol={user.rol}
                user={{ nombres: user.nombres, apellidos: user.apellidos }}
                onNavigate={setActiveKey}
              />
            </div>
          </main>
        </div>
      </div>

      <NotificacionToast />

      {showChangePwd && (
        <CambiarPasswordModal onClose={() => setShowChangePwd(false)} />
      )}
    </NotificacionProvider>
  );
};

export default DashboardLayout;
